/**
 * Unified Analytics Service
 *
 * Sends events to PostHog (product analytics) and GTM (Meta/Google attribution).
 * Use this service for ALL analytics - don't call posthog/gtm directly.
 */
import * as ph from "./posthog";
import * as gtm from "./gtm";
import * as tracking from "./tracking";

// Plan prices for purchase events
const PLAN_PRICES: Record<string, number> = {
  starter: 15, creator: 30, pro: 60,
  starter_new: 20, creator_new: 40, pro_new: 80,
  the_ultimate: 160, ultimate: 160,
};

// Helper to add timestamp
const withTimestamp = (props?: Record<string, unknown>) => ({
  ...props,
  timestamp: new Date().toISOString(),
});

// ============ CORE METHODS ============

export const identify = ph.identify;
export const track = ph.capture;
export const registerGlobalProperties = ph.registerGlobals;
export const getSessionId = ph.getSessionId;

// ============ DUAL EVENTS (PostHog + GTM) ============

export function trackPageView(pageName: string, pageTitle: string, userId?: string, props?: Record<string, unknown>) {
  const base = { page_title: pageTitle, page_path: window.location.pathname, user_id: userId, ...props };
  ph.capture(`${pageName} Page Viewed`, withTimestamp(base));
  gtm.gtmEvent("page_view", { ...base, page_location: window.location.href });
}

export function trackViewContent(toolName: string, userId?: string, props?: Record<string, unknown>) {
  const base = { tool_name: toolName, user_id: userId, page: window.location.pathname, ...props };
  ph.capture("Tool Page Viewed", withTimestamp(base));
  gtm.gtmEvent("view_content", withTimestamp({
    content_type: "tool", content_name: toolName, user_id: userId,
    page_location: window.location.href, page_path: window.location.pathname, ...props,
  }));
}

export function trackAddToCart(userId?: string, planType?: string) {
  ph.capture("Add to Cart", withTimestamp({ user_id: userId, plan_type: planType }));
  gtm.gtmEvent("add_to_cart", { user_id: userId, item_name: planType ? `${planType} plan` : undefined });
}

export function trackBeginCheckout(userId: string, planType: string, value: number, currency = "USD") {
  ph.capture("Checkout Initiated", withTimestamp({ user_id: userId, plan_type: planType, value, currency }));
  gtm.gtmEvent("begin_checkout", { currency, value, user_id: userId, plan_type: planType });
}

// ============ GTM ONLY ============

export function trackViewItem(itemCategory: string, itemName: string, toolType: string, userId?: string, props?: Record<string, unknown>) {
  gtm.gtmEvent("view_item", withTimestamp({
    item_category: itemCategory, item_name: itemName, tool_type: toolType,
    page_location: window.location.href, page_path: window.location.pathname,
    user_id: userId, is_logged_in: !!userId, ...props,
  }));
}

export function trackAddPaymentInfo(userId: string, planType: string, value: number, currency = "USD") {
  gtm.gtmEvent("add_payment_info", { currency, value, user_id: userId, plan_type: planType });
}

// ============ SIGNUP (with duplicate prevention) ============

/** Track signup - sends to BOTH PostHog and GTM */
export function trackSignUp(userId: string, userProps: Record<string, unknown>) {
  if (tracking.hasTrackedSignup(userId)) return;
  if (!tracking.isRecentSignup(userProps.created_at as string)) return;

  tracking.markSignupTracked(userId);
  const timeSince = Date.now() - new Date(userProps.created_at as string).getTime();

  // PostHog: User Signed Up
  ph.capture("User Signed Up", withTimestamp({
    user_id: userId, signup_method: "clerk", time_since_creation_ms: timeSince, ...userProps,
  }));

  // GTM: sign_up (for Meta attribution)
  gtm.gtmEvent("sign_up", withTimestamp({ method: "clerk", user_id: userId, time_since_creation_ms: timeSince }));
}

/** @deprecated Use trackSignUp instead - it now sends to both PostHog and GTM */
export function trackSignUpGTM(_userId: string, _userProps: Record<string, unknown>, _method = "clerk") {
  // No-op: trackSignUp now handles both PostHog and GTM
  // Kept for backwards compatibility, but does nothing to prevent duplicate events
}

// ============ PURCHASE (dual-layer prevention) ============

export async function trackPurchase(userId: string, planType: string, context: string, userEmail?: string, metaPurchaseEventSent?: boolean) {
  try {
    if (tracking.hasTrackedPurchase(userId)) return;

    // Check server flag
    if (metaPurchaseEventSent === true) {
      tracking.markPurchaseTracked(userId);
      return;
    }

    // Fetch fresh if not provided
    if (metaPurchaseEventSent === undefined) {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";
      try {
        const res = await fetch(`${API_BASE_URL}/users/${userId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.user?.meta_purchase_event_sent) {
            tracking.markPurchaseTracked(userId);
            return;
          }
        }
      } catch {
        return; // Don't track if we can't verify
      }
    }

    tracking.markPurchaseTracked(userId);
    gtm.gtmEvent("purchase", {
      transaction_id: `${userId}_${planType}_${Date.now()}`,
      value: PLAN_PRICES[planType] || 0,
      currency: "USD",
      user_id: userId,
      user_email: userEmail,
      plan_type: planType,
      context,
    });

    // Update server flag
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";
    fetch(`${API_BASE_URL}/purchase-events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    }).catch(() => {});
  } catch {
    // Ignore errors
  }
}

// ============ POSTHOG ONLY EVENTS ============

export function trackVideoGenerated(props: { user_id?: string; model: string; prompt_length: number; video_id: string; generation_type: string; [k: string]: unknown }) {
  ph.capture("Video Generated", props);
}

export function trackImageGenerated(props: { user_id?: string; model: string; prompt_length: number; image_id: string; generation_type: string; [k: string]: unknown }) {
  ph.capture("Image Generated", props);
}

export function trackOutOfCredits(props: { credits_remaining: number; credits_required: number; model_attempted: string; generation_type: string; is_paid_user: boolean; user_plan: string; [k: string]: unknown }) {
  ph.capture("Out of Credits - Generation Blocked", withTimestamp(props));
}

export function trackAssetDownloaded(assetId: string, assetType: "video" | "image", userId?: string, hasWatermark?: boolean) {
  ph.capture("Asset Downloaded", withTimestamp({ asset_id: assetId, asset_type: assetType, user_id: userId, has_watermark: hasWatermark }));
}

export function trackPromptCopied(assetId: string, assetType: "video" | "image", userId?: string) {
  ph.capture("Prompt Copied", withTimestamp({ asset_id: assetId, asset_type: assetType, user_id: userId }));
}

export function trackAssetFavorited(assetId: string, assetType: "video" | "image", userId?: string, isFavorite?: boolean) {
  ph.capture("Asset Favorited", withTimestamp({ asset_id: assetId, asset_type: assetType, user_id: userId, is_favorite: isFavorite }));
}

export function trackAutoPromptClicked(userId?: string, imageUrl?: string, sourcePage?: string) {
  ph.capture("Auto Prompt Clicked", withTimestamp({ user_id: userId, image_url: imageUrl, source_page: sourcePage }));
}

/**
 * Fires when a user clicks any PieCan hand-off CTA (notices, form banners,
 * the gating-modal link, or the /go/piecan email redirect). `source`
 * distinguishes the surface (home_notice, assets_notice, i2v_form,
 * image_edit_form, t2i_form, t2v_form, character_page, gating_modal,
 * email_redirect).
 */
export function trackPiecanHandoffClick(userId?: string, source?: string, props?: Record<string, unknown>) {
  ph.capture("PieCan Handoff Clicked", withTimestamp({
    user_id: userId,
    source,
    destination: "pixelmaker->piecan",
    ...props,
  }));
}

// ============ UTILITY ============

export const clearPurchaseTracking = tracking.clearPurchaseTracking;

// Default export as object for backwards compatibility
export const Analytics = {
  identify, track, registerGlobalProperties, getSessionId,
  trackPageView, trackViewContent, trackViewItem, trackAddToCart, trackBeginCheckout, trackAddPaymentInfo,
  trackSignUp, trackSignUpGTM, trackPurchase,
  trackVideoGenerated, trackImageGenerated, trackOutOfCredits,
  trackAssetDownloaded, trackPromptCopied, trackAssetFavorited, trackAutoPromptClicked,
  trackPiecanHandoffClick,
  clearPurchaseTracking,
};

export default Analytics;
